import React, { useState } from 'react';

import {
  Box,
  Link,
  Stack,
  Collapse,
  Typography,
  CircularProgress,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import {
  CommunicationActivityType,
  CommunicationTranscriptStatus,
} from 'src/services/integration/integrations.types';
import { useLazyGetCommunicationActivityContentQuery } from 'src/services/integration/integrations.service';

interface CommunicationActivityContentProps {
  activityId: string;
}

/**
 * On-demand message/transcript/summary for a single Dialpad (or other comms)
 * timeline row. Content is deliberately never part of the feed payload — it is
 * fetched from the permission-gated content endpoint when the row is expanded,
 * and re-fetched on every reopen so a transcript that backfills asynchronously
 * (applyTranscript, +2m/+10m/+30m) surfaces without reloading the page.
 */
const CommunicationActivityContent: React.FC<CommunicationActivityContentProps> = ({ activityId }) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);
  const [trigger, { data, isFetching, isError }] = useLazyGetCommunicationActivityContentQuery();

  const fetchContent = () => {
    // preferCacheValue = false -> always hit the server, so a previously PENDING
    // transcript is picked up when the user reopens the row later.
    trigger({ activityId }, false);
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      fetchContent();
    }
  };

  const renderBody = () => {
    if (isFetching) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {t('activities.feed.content.loading')}
          </Typography>
        </Stack>
      );
    }

    if (isError) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="error">
            {t('activities.feed.content.error')}
          </Typography>
          <Link component="button" type="button" variant="caption" onClick={fetchContent}>
            {t('activities.feed.content.retry')}
          </Link>
        </Stack>
      );
    }

    if (!data) {
      return null;
    }

    const isSms = data.activity_type === CommunicationActivityType.SMS;
    const hasMessage = !!data.message_body;
    const hasSummary = !!data.summary_text;
    const hasTranscript = !!data.transcript_text;
    const transcriptPending = data.transcript_status === CommunicationTranscriptStatus.PENDING;
    const transcriptUnavailable = data.transcript_status === CommunicationTranscriptStatus.UNAVAILABLE;

    const blocks: React.ReactNode[] = [];

    if (isSms) {
      blocks.push(
        hasMessage ? (
          <ContentBlock key="message" label={t('activities.feed.content.message')}>
            {data.message_body}
          </ContentBlock>
        ) : (
          <Muted key="no-message">{t('activities.feed.content.no_message')}</Muted>
        ),
      );
    } else {
      if (hasSummary) {
        blocks.push(
          <ContentBlock key="summary" label={t('activities.feed.content.summary')}>
            {data.summary_text}
          </ContentBlock>,
        );
      }
      if (hasTranscript) {
        blocks.push(
          <ContentBlock key="transcript" label={t('activities.feed.content.transcript')} scroll>
            {data.transcript_text}
          </ContentBlock>,
        );
      } else if (transcriptPending) {
        blocks.push(<Muted key="pending">{t('activities.feed.content.transcript_pending')}</Muted>);
      } else if (transcriptUnavailable) {
        blocks.push(<Muted key="unavailable">{t('activities.feed.content.transcript_unavailable')}</Muted>);
      }
    }

    if (blocks.length === 0) {
      return <Muted>{t('activities.feed.content.none')}</Muted>;
    }

    return <Stack spacing={1}>{blocks}</Stack>;
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      <Link component="button" type="button" variant="caption" onClick={toggle}>
        {open ? t('activities.feed.content.hide') : t('activities.feed.content.show')}
      </Link>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1 }}>{renderBody()}</Box>
      </Collapse>
    </Box>
  );
};

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary">
    {children}
  </Typography>
);

const ContentBlock: React.FC<{ label: string; scroll?: boolean; children: React.ReactNode }> = ({
  label,
  scroll,
  children,
}) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 1,
    }}
  >
    <Typography variant="overline" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant="body2"
      color="text.primary"
      sx={{
        whiteSpace: 'pre-wrap',
        ...(scroll ? { maxHeight: 200, overflowY: 'auto' } : {}),
      }}
    >
      {children}
    </Typography>
  </Box>
);

export default CommunicationActivityContent;
